import { useEffect, useState, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

/**
 * Props:
 *  - title:    标题
 *  - trainKey: 数据中训练曲线字段（默认 "train"）
 *  - valKey:   数据中验证曲线字段（默认 "val"）
 *  - trainColor / valColor: 线条颜色（默认按你要的：Train=蓝、Val=橙）
 *  - nameTrain / nameVal: 图例显示名（默认 "Train" / "Validation"）
 */
export default function SingleLossChart({
  title = "Loss",
  trainKey = "train",
  valKey = "val",
  // 采用常见蓝/橙配色（接近你截图）：#1f77b4 / #ff7f0e
  trainColor = "#1f77b4",
  valColor = "#ff7f0e",
  nameTrain = "Train",
  nameVal = "Validation",
}) {
  const [data, setData] = useState([]);
  const chartRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchSeries = async () => {
      try {
        const res = await axios.get("/api/training-log/", {
          params: { trainKey, valKey, last: 3000 },
        });
        if (!res || !res.data || res.data.ok !== true || !Array.isArray(res.data.series)) {
          console.warn("Unexpected payload from /api/training-log/:", res?.data);
          return;
        }
        const parsed = res.data.series.map((row) => ({
          epoch: row.epoch != null ? Number(row.epoch) : null,
          [trainKey]: row.train != null ? Number(row.train) : null,
          [valKey]: row.val != null ? Number(row.val) : null,
        }));
        if (!cancelled) setData(parsed);
      } catch (e) {
        console.error("Failed to fetch data", e);
      }
    };

    fetchSeries();
    const id = setInterval(fetchSeries, 30000); // 30s 刷新
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [trainKey, valKey]);

  const handleExport = () => {
    if (!chartRef.current) return;
    html2canvas(chartRef.current).then((canvas) => {
      const link = document.createElement("a");
      link.download = `${String(title || "chart").replace(/\s+/g, "_")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    });
  };

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <button
        onClick={handleExport}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          background: "white",
          border: "1px solid #ccc",
          borderRadius: "4px",
          padding: "2px 6px",
          fontSize: "12px",
          cursor: "pointer",
          zIndex: 10,
        }}
        title="导出图表为 PNG"
      >
        ⬇️
      </button>

      <div ref={chartRef} style={{ height: "100%", paddingTop: 6 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>{title}</div>

        <ResponsiveContainer width="100%" height="85%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="epoch" />
            <YAxis />
            <Tooltip />
            <Legend />
            {/* 固定蓝/橙配色与图例名称 */}
            <Line
              type="monotone"
              dataKey={trainKey}
              name={nameTrain}
              dot={false}
              stroke={trainColor}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey={valKey}
              name={nameVal}
              dot={false}
              stroke={valColor}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
